import RichText from "~/components/RichText";
import { type ContentSection_Fields } from "~/__generated__/graphql";
import CustomImage from "~/components/CustomImage";
import CustomLink from "~/components/CustomLink";
import { cn } from "~/utils/cn";
import IframeWrapper from "~/components/IframeWrapper";
import Icon from "~/components/Icon/Icon";
import ContentButton from "~/components/ContentButton";
import Story from "~/components/Story";

export default function ContentSection({
  type,
  label,
  title,
  description,
  image,
  video,
  orientation,
  button,
  backgroundColor,
  quote,
  className,
  story,
}: ContentSection_Fields & { className?: string }) {
  return (
    <section
      className={cn(
        "grid grid-cols-1 justify-between gap-5 px-6 py-10 md:grid-cols-2 lg:gap-8 lg:px-24 xl:gap-5 xl:px-48",
        backgroundColor?.[0] === "White"
          ? "bg-white"
          : backgroundColor?.[0] === "Blue"
            ? "bg-black"
            : backgroundColor?.[0] === "Grey"
              ? "bg-grey"
              : "bg-white",
        (type?.[0] === "Text" || (type?.[0] === "Quote" && !image)) && "flex",
        className,
      )}
    >
      <div
        className={cn(
          "flex flex-1",
          type?.[0] === "Text" && "block",
          type?.[0] === "Quote" && "self-center",
          orientation?.[0] === "Text Right" && "lg:order-last",
        )}
      >
        {type?.[0] === "Quote" && (
          <span className="mr-3">
            <Icon
              type="quote"
              className={
                backgroundColor?.[0] === "Blue" ? "fill-white" : "fill-black"
              }
            />
          </span>
        )}
        <div className="inline-flex flex-1 flex-col gap-3">
          {label && (
            <span className="w-fit border-l-4 border-black bg-zinc-100 px-2 py-1 text-[0.8125rem] text-gray-800">
              {label}
            </span>
          )}
          {title && (
            <h2
              className={cn(
                "text-4xl text-gray-800",
                backgroundColor?.[0] === "Blue" && "text-white",
              )}
            >
              {title}
            </h2>
          )}
          {description && (
            <RichText
              field={description}
              className={cn(
                "text-slate-500",
                backgroundColor?.[0] === "Blue" && "text-white",
              )}
            />
          )}
          {type?.[0] === "Quote" && quote && (
            <div
              className={cn(
                "text-slate-500",
                backgroundColor?.[0] === "Blue" && "text-white",
              )}
            >
              <div className="mb-1 text-base">{quote.customerName}</div>
              <div className="text-xs">{quote.customerRole}</div>
            </div>
          )}
          {button && <ContentButton {...button} />}
        </div>
      </div>
      {(type?.[0] === "Image" || type?.[0] === "Quote") && image && (
        <div className="flex justify-center sm:min-h-[20rem]">
          <CustomImage
            data={image}
            className="h-auto min-h-full w-fit object-contain sm:h-0"
            sizes="(min-width: 1980px) 474px, (min-width: 1300px) calc(3.94vw + 397px), (min-width: 1260px) calc(-180vw + 2742px), (min-width: 1040px) calc(33vw + 65px), (min-width: 780px) calc(49.17vw - 28px), (min-width: 640px) 474px, calc(100vw - 48px)"
          />
        </div>
      )}
      {type?.[0] === "Video" && video && <IframeWrapper video={video} />}
      {type?.[0] === "Flipbook" && (
        <IframeWrapper
          icon="fullscreen"
          ignoreCookies
          video={{
            preview: image,
            source: ["Url"],
            url: "/flipbook/iframe.html?hash=OTU1RjlFNkQ3NUUrdWk0MXMwOGNqbA==",
          }}
        />
      )}
      {type?.[0] === "Story" && (
        <Story
          title={story?.title}
          description={story?.description}
          label={story?.label}
          type={story?.type}
          video={story?.video}
          image={story?.image}
        />
      )}
    </section>
  );
}
